import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { forcedNewDesign } from '../../hooks/useNewDesign'
import { ListSummary } from '../../api/FolderApi'
import {
  Card, Column, Columns, Notification
} from 'bloomer'
import { PureComponent } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { Trans, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { FilterLocation, setProjects } from '../../actions/filterActions'
import api from '../../api'
import { ICorporationSetting } from '../../api/types/corporation'
import { FolderMode } from '../../components/checklists/result/folders/Folder'
import FolderWrapper from '../../components/checklists/result/folders/FolderWrapper'
import ScheduledChecklistsTable from '../../components/checklists/result/ScheduledChecklistsTable'
import { PowerUserView } from '../../components/PowerUserView'
import TabPanel, { a11yProps } from '../../components/TabPanel'
import StartWithTemplate from '../../components/template_store/StartWithTemplate'
import ActiveFilterMessage from '../../components/widgets/ActiveFilterMessage'
import { IAlphaprocessState } from '../../store'
import ISelectListValueModel from '../../types/selectListModel'
import ITranslationProps from '../../types/translationProps'
import ResultView from './ResultView/ResultView'

interface IState {
  openChecklist: number | null
  currentTab: number
  isFullscreen: boolean
  hasTemplates: boolean
  hasInvited: boolean
  folderId: string | null
  // new design: number of open processes for the tab label (list counts)
  openCount: number | null
}

interface IStateProps {
  corporationSettings?: ICorporationSetting | null
  isLoggedIn?: boolean | null
  isOffline: boolean
  isAdmin: boolean
  canEditTemplates: boolean
  newDesign: boolean
}

interface IDispatchProps {
  setProjects: (projects: Array<ISelectListValueModel<any>>) => void
}

interface IParams {
  id: string
  folderId?: string | null
  templateId?: string | null
  tabIndex?: string | null
}

type Props = IStateProps & RouteComponentProps<IParams> & IDispatchProps & ITranslationProps

const ResultColumn = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: '25%',
    marginLeft: '25%'
  }
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    '&:not(:last-of-type)': {
      marginRight: theme.spacing(1)
    }
  }
}))

class ProcessOverview extends PureComponent<Props, IState> {
  constructor(props: Props) {
    super(props)
    const { id, tabIndex, folderId } = this.props.match.params
    const openChecklist = id ? parseInt(id) : null
    this.state = {
      openChecklist,
      currentTab: tabIndex ? parseInt(tabIndex) : 0,
      isFullscreen: false,
      hasTemplates: true,
      hasInvited: false,
      openCount: null,
      folderId: folderId ?? null
    }
  }

  public async componentDidMount() {
    const { id, templateId, tabIndex } = this.props.match.params

    const urlParams = new URLSearchParams(this.props.location.search)
    const accessToken = urlParams.get('access_token')

    if (typeof tabIndex === 'undefined' && !accessToken && !id && !templateId) {
      this.handleChangeTab(this.state.currentTab, true)
    }
    const fullscreen = urlParams.get('fullscreen')

    // new design: a process opened by its address (start, dashboard, links) opens in the panel
    // next to the list like one picked from the list; the page only with ?fullscreen=1
    const pageByAddress = !!(id && templateId) && !(this.props.newDesign && !isMobileOnly)
    if (pageByAddress || fullscreen) {
      this.setState({
        isFullscreen: true
      })
    }

    // User accesses checklist authenticated with a token
    if (this.isExternalAccess()) {
      return
    }

    if (!this.isExternalAccess() && !this.props.isLoggedIn) {
      this.props.history.push('/login')
    }

    const params = new URLSearchParams(this.props.location.search)
    const project_id = params.get('project_id')
    const project_name = params.get('project_name')
    if (project_id) {
      this.props.setProjects([{ value: parseInt(project_id), label: project_name }])
    }

    // Only people who may edit templates may list them (the backend answers 401 otherwise). Before,
    // every employee got that 401 here, it stopped this method and the "Eingeladen" check below
    // never ran (largest Sentry issue, 2,3K events).
    if (this.props.canEditTemplates) {
      try {
        const templates = await api.checklists.all({ page: 1 })
        if (templates?.totalCount === 0) {
          this.setState({ hasTemplates: false })
        }
      } catch (_) {
        // the empty-state hint is optional
      }
    }

    // Check whether the user has any invited checklists anywhere in the folder
    // tree. Uses Total-Result-Count which counts only checklist items, not folders.
    try {
      const hasInvited = await api.folders.hasInvitedChecklists()
      if (hasInvited) {
        this.setState({ hasInvited: true })
      }
    } catch (_) {
      // On error keep the tab visible rather than hiding it
    }
  }

  public componentDidUpdate(prevProps: Props) {
    if (this.props.match.params.id !== prevProps.match.params.id) {
      this.setState({
        openChecklist: this.props.match.params.id
          ? parseInt(this.props.match.params.id) : null
      })
    }

    if (this.props.match.params.folderId !== prevProps.match.params.folderId) {
      this.setState({
        folderId: this.props.match.params.folderId
      })
    }

    if (this.props.match.params.tabIndex !== prevProps.match.params.tabIndex) {
      const currentTab = Number.isInteger(parseInt(this.props.match.params.tabIndex))
        ? parseInt(this.props.match.params.tabIndex) : 0
      // Preserve folderId from URL params when present. Without this, navigating from
      // a URL without tabIndex (e.g. /checklists) into a folder URL
      // (e.g. /checklists/tab/0/folders/:id) triggers the tabIndex-change branch and
      // resets folderId to null, clobbering the folder navigation in the same render.
      this.setState({ currentTab, folderId: this.props.match.params.folderId ?? null })
    }

    if (!this.props.isLoggedIn && prevProps.isLoggedIn && !this.isExternalAccess()) {
      this.props.history.push('/login')
    }
  }

  private handleResultSetViewClosed = (reload: boolean) => {
    this.setState({ openChecklist: null })
    if (reload) {
      // closed process: on to "Erledigt"
      this.setState({ currentTab: 1, folderId: null })
      this.props.history.replace('/checklists/tab/1')
      return
    }
    // back to the same tab and folder; the list stays as it is (no new loading)
    const { currentTab, folderId } = this.state
    this.props.history.replace(folderId
      ? `/checklists/tab/${currentTab}/folders/${folderId}`
      : `/checklists/tab/${currentTab}`)
  }

  private handleUpdateCurrentFolder = (folderId: string) => {
    this.setState({ folderId })

    const url = `/checklists/tab/${this.state.currentTab}/folders/${folderId}`
    this.props.history.push(url)
  }

  // @ts-ignore
  private handleSelect = ({ id }) => {
    this.setState({ openChecklist: id })

    this.props.history.push(`/checklists/${id}`)
  }

  private handleChangeTab = (tabIndex: number, noPush = false) => {
    this.setState({ currentTab: tabIndex, folderId: null })

    const url = `/checklists/tab/${tabIndex}`
    if (!noPush) {
      this.props.history.push(url)
    }
  }

  // toggle fullscreen
  private handleFullScreen = (resultSet) => {
    const isFullscreen = !this.state.isFullscreen
    const url = isFullscreen
      ? `/checklists/${resultSet.checklist_id}/audit/${resultSet.id}${this.props.newDesign ? '?fullscreen=1' : ''}`
      : `/checklists/${resultSet.id}`

    this.props.history.push(url)

    this.setState({
      isFullscreen
    })
  }

  private handleOpenSummary = (summary: ListSummary | null) => {
    const openCount = summary ? summary.total : null
    if (openCount !== this.state.openCount) this.setState({ openCount })
  }

  private isExternalAccess = () => {
    if (this.props.isLoggedIn) {
      return false
    }

    const urlParams = new URLSearchParams(this.props.location.search)
    const accessToken = urlParams.get('access_token')
    return !!accessToken
  }

  public render() {
    const { isOffline, t } = this.props
    const {
      currentTab, openChecklist, isFullscreen, folderId
    } = this.state
    const desktopColumnSize = openChecklist ? '1/2' : 'full'
    const wideScreenColumnSize = openChecklist ? 7 : 'full'

    const resultViewContent = (
      <Card className="result-view-card">
        <ResultView
          resultSetId={openChecklist}
          onClose={this.handleResultSetViewClosed}
          context="checklists"
          onFullscreen={this.handleFullScreen}
          isFullscreen={isFullscreen}
          isExternalAccess={this.isExternalAccess()}
          tabIndex={currentTab}
          folderId={folderId}
        />
      </Card>
    )
    const resultView = openChecklist && !isMobileOnly ? (
      <Column id="result-column">
        { resultViewContent }
      </Column>
    ) : null

    // external users that have no account see only the checklist and not the columns based layout
    if (openChecklist && (this.isExternalAccess() || isMobileOnly)) {
      return (
        <ResultColumn id="result-column">
          { resultViewContent }
        </ResultColumn>
      )
    }

    // required because without this it will crash if a user does logout while he is on this page
    if (!this.isExternalAccess() && !this.props.isLoggedIn) {
      return null
    }

    // Doesn't accept string[] but can't import AllPlatforms[] type from bloomer
    const hideOverview: any[] = (openChecklist && isFullscreen)
      || (isMobileOnly && openChecklist)
      ? ['mobile', 'desktop', 'tablet'] : null

    const containerClass = clsx(
      'checklist-overview',
      isFullscreen && 'has-fullscreen-margin',
      // new design: list with a fixed width next to the open process (drafts 1b / 1c)
      this.props.newDesign && openChecklist && !isFullscreen && !isMobileOnly && 'nd-split'
    )

    return (
      <div className={containerClass}>
        <Columns className="overview-columns">
          <Column
            isSize={
              {
                mobile: desktopColumnSize,
                tablet: desktopColumnSize,
                desktop: desktopColumnSize,
                widescreen: wideScreenColumnSize
              }
            }
            isHidden={hideOverview}
            className="overview-content"
          >
            <ActiveFilterMessage filterLocation={FilterLocation.checklists} />
            <Notification
              className="is-hidden-tablet"
              isColor="info"
              isHidden={this.state.hasTemplates}
            >
              <Trans>overview.createTemplatesOnDesktop</Trans>
            </Notification>
            <Box
              sx={this.props.newDesign ? {
                display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, borderBottom: '1px solid #C3CCD8'
              } : { display: 'contents' }}
            >
              <Tabs
                value={currentTab}
                onChange={(e, index) => this.handleChangeTab(index)}
                className={this.props.newDesign ? 'nd-tabs' : undefined}
                sx={this.props.newDesign ? { flex: '1 1 auto', minWidth: 0, borderBottom: '0 !important' } : { mb: { xs: 1, md: 2 }, ml: { sm: 2 } }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <StyledTab
                  label={this.props.newDesign && this.state.openCount !== null ? (
                    <span>
                      {t('overview.tabs.open')}
                      <span className="nd-tab-count">{this.state.openCount}</span>
                    </span>
                  ) : t('overview.tabs.open')}
                  {...a11yProps(0)}
                />
                <StyledTab label={t('overview.tabs.closed')} {...a11yProps(1)} />
                { this.props.corporationSettings.use_schedules && (
                <StyledTab
                  label={t('overview.tabs.scheduled')}
                  disabled={isOffline}
                  sx={{ display: isOffline ? 'none' : undefined }}
                  {...a11yProps(2)}
                />
                ) }
                { this.state.hasInvited && (
                <StyledTab label={t('overview.tabs.invited')} {...a11yProps(3)} />
                ) }
                <StyledTab label={t('overview.tabs.templates')} {...a11yProps(4)} />
              </Tabs>
              {/* new design, desktop: "Neue Vorlage" / "Prozess starten" of the list land here (portal) */}
              {this.props.newDesign && <Box id="nd-tab-actions" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 'auto', flex: 'none' }} />}
            </Box>
            {!this.props.isOffline && (
            <PowerUserView>
              <StartWithTemplate compact={!!openChecklist} />
            </PowerUserView>
            )}
            <TabPanel index={0} value={currentTab}>
              <FolderWrapper
                onSelect={this.handleSelect}
                selectedId={openChecklist}
                mode={FolderMode.checklists}
                checklistStatus="open"
                currentFolderId={folderId}
                onUpdateCurrentFolder={this.handleUpdateCurrentFolder}
                tabIndex={currentTab}
                onSummary={this.handleOpenSummary}
              />
            </TabPanel>
            <TabPanel index={1} value={currentTab}>
              <FolderWrapper
                onSelect={this.handleSelect}
                checklistStatus="closed"
                mode={FolderMode.checklists}
                currentFolderId={folderId}
                onUpdateCurrentFolder={this.handleUpdateCurrentFolder}
                tabIndex={currentTab}
              />
            </TabPanel>
            { this.props.corporationSettings.use_schedules && (
            <TabPanel index={2} value={currentTab}>
              <ScheduledChecklistsTable />
            </TabPanel>
            ) }
            { this.state.hasInvited && (
            <TabPanel index={3} value={currentTab}>
              <FolderWrapper
                onSelect={this.handleSelect}
                checklistStatus="invited"
                mode={FolderMode.checklists}
                currentFolderId={folderId}
                onUpdateCurrentFolder={this.handleUpdateCurrentFolder}
                tabIndex={currentTab}
              />
            </TabPanel>
            ) }
            <TabPanel index={4} value={currentTab}>
              <FolderWrapper
                onSelect={this.handleSelect}
                checklistStatus="open"
                mode={FolderMode.templates}
                currentFolderId={folderId}
                onUpdateCurrentFolder={this.handleUpdateCurrentFolder}
                tabIndex={currentTab}
              />
            </TabPanel>
          </Column>
          { resultView }
        </Columns>

      </div>
    )
  }
}

const mapState = (state: IAlphaprocessState): IStateProps => (
  {
    corporationSettings: state.core.user?.corporation?.corporation_setting,
    isLoggedIn: !!state.core.jwt,
    isOffline: state.core.isOffline,
    isAdmin: state.core.user?.current_role === 'admin',
    newDesign: !!state.core.user?.new_design_enabled || forcedNewDesign(),
    canEditTemplates: state.core.user?.current_role === 'admin' || !!state.core.user?.can_edit_checklists
  })

const mapDispatch = (dispatch: (x: any) => void): IDispatchProps => ({
  setProjects: (projects: Array<ISelectListValueModel<any>>) => dispatch(
    setProjects(projects, FilterLocation.checklists)
  )
})

export default connect(mapState, mapDispatch)(withTranslation()(ProcessOverview))
